import { useCallback, useEffect, useRef, useState } from "react";

const RETRY_STEPS_MS = [3000, 6000, 12000, 20000, 30000];

/**
 * Fetch now, then again every `intervalMs`, as the inbox's safety net under
 * the live stream (useInboxStream). The stream says what changed; this makes
 * sure nothing is missed if an event is.
 *
 *   One request at a time: the next one is scheduled when the last settles,
 *   never on a fixed clock, so a slow response cannot pile up.
 *   Paused while the browser tab is hidden; fetched again the moment it shows.
 *   A failure keeps what is on screen and retries on a backoff (3s up to 30s)
 *   until the API answers again. It used to stop for good on the first
 *   failure, so a restart of the API left the inbox on "could not load" until
 *   someone pressed Try again. Only a 401/403 stops it: retrying cannot fix
 *   a missing grant.
 *   Only the newest request writes state: switching conversations faster than
 *   a response arrives must not show the old one.
 *
 * Nothing is cached: this is people's phone numbers and messages.
 */
export default function usePolling(fetchFn, key, intervalMs, { skip = false } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState(null);
  const [runToken, setRunToken] = useState(0);
  const fetchRef = useRef(fetchFn);
  fetchRef.current = fetchFn;
  const lastKey = useRef(null);
  const intervalRef = useRef(intervalMs);
  intervalRef.current = intervalMs;

  useEffect(() => {
    if (skip) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    let timer = null;
    let controller = null;
    let failures = 0;

    // A different conversation or filter starts blank; a refresh of the same
    // one keeps what is on screen until the new answer arrives.
    if (lastKey.current !== key) {
      lastKey.current = key;
      setData(null);
      setLoading(true);
      setError(null);
    }

    const schedule = (ms) => {
      clearTimeout(timer);
      if (cancelled || document.hidden) return;
      timer = setTimeout(run, ms);
    };

    function run() {
      clearTimeout(timer);
      controller?.abort();
      controller = new AbortController();
      fetchRef
        .current(controller.signal)
        .then((res) => {
          if (cancelled) return;
          failures = 0;
          setData(res ?? null);
          setError(null);
          setLoading(false);
          schedule(intervalRef.current);
        })
        .catch((err) => {
          if (cancelled || err?.name === "AbortError") return;
          setError(err || new Error("request failed"));
          setLoading(false);
          if (err?.Status === 401 || err?.Status === 403) return;
          schedule(RETRY_STEPS_MS[Math.min(failures, RETRY_STEPS_MS.length - 1)]);
          failures += 1;
        });
    }

    const onVisibility = () => {
      if (!document.hidden && !cancelled) run();
      else clearTimeout(timer);
    };

    run();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", run);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller?.abort();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", run);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, skip, runToken]);

  /** Fetch again now (a live event, an action, or "Try again"). */
  const refresh = useCallback(() => setRunToken((n) => n + 1), []);

  return { data, loading, error, refresh };
}
